import { type ReactNode } from 'react';
import { Modal } from '../Modal/Modal';
import { Button } from '../Button/Button';

export interface DialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onConfirm?: () => void;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly destructive?: boolean;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  readonly loading?: boolean;
  readonly children?: ReactNode;
}

/**
 * Confirmation dialog — built on Modal. Use for destructive actions
 * (cancel booking, delete profile, …) or any yes/no decision that warrants
 * blocking the page. For form input, use Modal directly.
 */
export function Dialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  destructive = false,
  confirmLabel = destructive ? 'Confirm' : 'OK',
  cancelLabel = 'Cancel',
  loading = false,
  children,
}: DialogProps) {
  return (
    <Modal
      open={open}
      onClose={() => {
        if (!loading) onClose();
      }}
      showCloseButton={!loading}
      dismissOnBackdrop={!loading}
      dismissOnEscape={!loading}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'primary'}
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
    </Modal>
  );
}
